/**
 * Utilities for resilient lazy loading of async chunks.
 *
 * Webpack code-splits the Vue app into content-hashed chunks. Two failure modes
 * cause the dreaded `ChunkLoadError: Loading chunk N failed`:
 *
 *  1. STALE TAB AFTER DEPLOY — a user has an old page/entry bundle loaded in a
 *     long-lived tab. We deploy, the build replaces the chunk files, and the
 *     old bundle then requests a chunk filename that no longer exists on the
 *     server. The import 404s / times out. The only real fix is a fresh page
 *     load so the browser pulls the new entry bundle. See `reloadOnChunkError`.
 *
 *  2. TRANSIENT NETWORK BLIP — the chunk exists but the request timed out (slow
 *     connection, flaky CDN edge). A simple retry usually recovers without a
 *     full reload. See `lazyImport`.
 *
 * `lazyImport` handles (2) with a bounded retry, then falls through to (1) by
 * triggering a one-time reload for anything that still fails.
 */
/** Matches webpack's chunk-load failures across browsers (name and message). */
function isChunkLoadError(error) {
    if (!error)
        return false;
    const name = error.name ?? '';
    const message = error.message ?? '';
    return (name === 'ChunkLoadError' ||
        /Loading chunk [\w-]+ failed/i.test(message) ||
        /Loading CSS chunk [\w-]+ failed/i.test(message) ||
        /error loading dynamically imported module/i.test(message));
}
const RELOAD_FLAG = 'ilb:chunk-reload';
/**
 * Force a single full page reload to recover from a stale entry bundle after a
 * deploy. Guarded by sessionStorage so we never loop: if the page comes back
 * and the chunk is *still* missing (a genuinely broken deploy rather than a
 * stale tab), we stop and let the error surface instead of reloading forever.
 *
 * @returns true if a reload was triggered, false if we already reloaded once.
 */
export function reloadOnChunkError() {
    try {
        if (sessionStorage.getItem(RELOAD_FLAG)) {
            return false;
        }
        sessionStorage.setItem(RELOAD_FLAG, '1');
    }
    catch {
        // sessionStorage can throw in private mode / sandboxed iframes; reload anyway.
    }
    window.location.reload();
    return true;
}
/** Clear the reload guard once the app has successfully booted. */
export function clearChunkReloadFlag() {
    try {
        sessionStorage.removeItem(RELOAD_FLAG);
    }
    catch {
        // ignore
    }
}
/**
 * Wrap a dynamic `import()` with transient-failure retries and, as a last
 * resort, a one-time page reload to recover from a post-deploy stale bundle.
 *
 * @param factory - the `() => import('./Foo.vue')` loader
 * @param options - retry tuning
 */
export function lazyImport(factory, options = {}) {
    const { retries = 2, delay = 400 } = options;
    return () => new Promise((resolvePromise, rejectPromise) => {
        const attempt = (remaining) => {
            factory()
                .then(resolvePromise)
                .catch((error) => {
                if (isChunkLoadError(error)) {
                    if (remaining > 0) {
                        setTimeout(() => attempt(remaining - 1), delay * (retries - remaining + 1));
                        return;
                    }
                    // Retries exhausted: the chunk is very likely gone because a
                    // deploy replaced it. Reload once to pull the fresh bundle.
                    if (reloadOnChunkError()) {
                        // Leave the promise pending — the page is navigating away.
                        return;
                    }
                }
                rejectPromise(error);
            });
        };
        attempt(retries);
    });
}
